/**
 * Capability policies — locale is one input among product, entitlements, session.
 * Authoritative capabilities for gated UI come from session (server-resolved).
 * These policies are defaults / optimistic hints only.
 */

/** @typedef {Record<string, boolean>} Capabilities */

const ALL_CAPABILITIES = Object.freeze({
  dashboard: true,
  booking: true,
  payments: true,
  flights: true,
  hotels: true,
  packages: true,
  organizedTours: true,
  admin: true,
});

const EN_AGENT_DEFAULTS = Object.freeze({
  dashboard: true,
  booking: false,
  payments: false,
  flights: false,
  hotels: true,
  packages: false,
  organizedTours: false,
  admin: false,
});

const EMPTY_CAPABILITIES = Object.freeze({
  dashboard: false,
  booking: false,
  payments: false,
  flights: false,
  hotels: false,
  packages: false,
  organizedTours: false,
  admin: false,
});

/** Baseline policy matrix — overridden by session.capabilities when present. */
export const CAPABILITY_POLICY_BASELINE = Object.freeze({
  he: Object.freeze({
    agent: ALL_CAPABILITIES,
    public: Object.freeze({
      dashboard: true,
      booking: true,
      payments: true,
      flights: true,
      hotels: true,
      packages: true,
      organizedTours: true,
      admin: false,
    }),
    admin: Object.freeze({
      ...ALL_CAPABILITIES,
      admin: true,
    }),
  }),
  en: Object.freeze({
    agent: EN_AGENT_DEFAULTS,
    public: Object.freeze({
      dashboard: true,
      booking: false,
      payments: false,
      flights: false,
      hotels: true,
      packages: false,
      organizedTours: false,
      admin: false,
    }),
    admin: EMPTY_CAPABILITIES,
  }),
  el: Object.freeze({
    agent: EN_AGENT_DEFAULTS,
    public: Object.freeze({
      dashboard: true,
      booking: false,
      payments: false,
      flights: false,
      hotels: true,
      packages: false,
      organizedTours: false,
      admin: false,
    }),
    admin: EMPTY_CAPABILITIES,
  }),
});

function normalizeLocale(locale) {
  const lng = String(locale || "he").toLowerCase();
  if (lng.startsWith("en")) return "en";
  if (lng.startsWith("el")) return "el";
  return "he";
}

function normalizeProduct(product) {
  return String(product || "agent").toLowerCase();
}

function mergeCapabilities(base, overrides) {
  if (!overrides || typeof overrides !== "object") return { ...base };
  const merged = { ...base };
  for (const [key, value] of Object.entries(overrides)) {
    if (typeof value === "boolean") merged[key] = value;
  }
  return merged;
}

/**
 * @param {{ locale?: string, product?: string, entitlements?: object|null, session?: object|null }} input
 * @returns {Capabilities}
 */
export function resolveCapabilities({ locale, product, entitlements = null, session = null }) {
  const lng = normalizeLocale(locale);
  const prod = normalizeProduct(product);

  const sessionCaps =
    session?.capabilities && typeof session.capabilities === "object"
      ? session.capabilities
      : null;

  if (sessionCaps) {
    return mergeCapabilities(EMPTY_CAPABILITIES, sessionCaps);
  }

  const baselineByLocale = CAPABILITY_POLICY_BASELINE[lng] || CAPABILITY_POLICY_BASELINE.he;
  const baseline = baselineByLocale[prod] || baselineByLocale.agent || EMPTY_CAPABILITIES;

  if (entitlements?.capabilities && typeof entitlements.capabilities === "object") {
    return mergeCapabilities(baseline, entitlements.capabilities);
  }

  return { ...baseline };
}

export function isCapabilityEnabled(capabilities, capabilityKey) {
  if (!capabilityKey) return true;
  return Boolean(capabilities?.[capabilityKey]);
}
